"use client";

import { signOut as signOutAction } from "@/lib/actions";
import { createClient } from "@/lib/supabase/client";
import { AuthError, type User } from "@supabase/supabase-js";
import { type ReactNode, createContext, useContext, useEffect, useRef, useState } from "react";

// Types
interface AuthContextType {
  user: User | null;
  isAdmin: boolean;
  isLoading: boolean;
  signOut: () => Promise<void>;
}

// Create the context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Provider component
export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const authChecked = useRef(false);
  const supabase = useRef(createClient());

  useEffect(() => {
    // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: User authentication requires complex session and admin verification logic
    async function getUser() {
      if (authChecked.current) return;

      setIsLoading(true);
      try {
        // First, try to get the session without refreshing
        const { data: sessionData } = await supabase.current.auth.getSession();

        if (sessionData?.session) {
          // If we have a session, use it to get the user
          const userData = sessionData.session.user;

          if (userData) {
            try {
              // Check admin status using RBAC permissions
              const { data: hasAdminPermissions } = await supabase.current.rpc(
                "check_multiple_permissions",
                {
                  p_user_id: userData.id,
                  p_permissions: [
                    "roles.create",
                    "roles.edit",
                    "roles.delete",
                    "permissions.assign",
                    "users.assign_roles",
                  ],
                }
              );

              // User is admin if they have any admin-level permissions
              const isAdminUser =
                hasAdminPermissions?.some((p: { has_permission: boolean }) => p.has_permission) ||
                false;
              setIsAdmin(isAdminUser);
            } catch (adminError) {
              console.error("Error checking admin status:", adminError);
              setIsAdmin(false);
            }

            setUser(userData);
          } else {
            setUser(null);
            setIsAdmin(false);
          }
        } else {
          // No session found, user is not logged in
          setUser(null);
          setIsAdmin(false);
        }
      } catch (error) {
        // Handle specific auth errors
        if (
          error instanceof AuthError &&
          (error.code === "refresh_token_not_found" ||
            error.code === "token_refreshed" ||
            error.code === "session_not_found")
        ) {
          // Auth session expired; sign out silently
          // Clear any stale auth state
          try {
            await supabase.current.auth.signOut();
          } catch (e) {
            console.error("Error signing out after auth error:", e);
          }
        } else {
          console.error("Error fetching user:", error);
        }

        // Set user to null regardless of error type
        setUser(null);
        setIsAdmin(false);
      } finally {
        setIsLoading(false);
        authChecked.current = true;
      }
    }

    getUser();
  }, []);

  // Handle logout with error handling
  const signOut = async () => {
    try {
      setIsLoading(true);

      // Use the server action for sign out
      const result = await signOutAction();

      if (result.success) {
        window.location.href = "/";
      }
    } catch (error) {
      console.error("Error during logout:", error);

      // Even if there's an error with the server action, try to sign out directly
      try {
        await supabase.current.auth.signOut();
      } catch (supabaseError) {
        console.error("Supabase signOut error:", supabaseError);
      }

      // Navigate to home regardless of errors
      window.location.href = "/";
    }
  };

  const value = {
    user,
    isAdmin,
    isLoading,
    signOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// Custom hook to use the auth context
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
